import { Injectable, inject } from '@angular/core';
import { captureException } from '@sentry/angular';
import { GoogleEventTypes } from '../../enum/datalayer-event-types';
import { GoogleTagManagerService } from '../google-tag-manager.service';
import {
  SelectItemEvent,
  DefaultEcommerceEvent,
  PaymentInfoEvent,
  PurchaseEvent,
  ShippingInfoEvent
} from '../../models/datalayer.model';
@Injectable({
  providedIn: 'root'
})
export class DataLayerService {
  private googleTagService = inject(GoogleTagManagerService);


  public pushGoogleEvent(eventName: GoogleEventTypes, data: any) {
    try {
      this.pushToDataLayer(eventName, data);
    } catch (error) {
      // console.error(error);
      captureException(error);
    }
  }

  private pushToDataLayer(eventName: GoogleEventTypes, data: any) {
    const serializeFunction = this.eventMapper[eventName];
    if (!serializeFunction) {
      throw new Error(
        `No serialization function found for event: ${eventName}`
      );
    }

    const serializedData = serializeFunction(data);
    if (serializedData?.items && serializedData?.items.length > 0) {
      serializedData.items[0] = {
        ...serializedData?.items[0],
        ...this.categoryProps(data.categories)
      };
    }
    this.checkForUndefinedProperties(eventName, serializedData);
    this.googleTagService.sendGoogleEvent(eventName, serializedData);
  }

  private readonly eventMapper: { [eventName: string]: (data: any) => any } = {
    select_item: (data) => this.selectItemSerializer(data),
    view_item: (data) => this.viewItemSerializer(data),
    add_to_cart: (data) => this.cartSerializer(data),
    begin_checkout: (data) => this.cartSerializer(data),
    add_shipping_info: (data) => this.shippingSerializer(data),
    add_payment_info: (data) => this.paymentInfoSerializer(data),
    purchase: (data) => this.purchaseSerializer(data)
  };

  private checkForUndefinedProperties(eventName: string, data: any) {
    const items = data.items;
    this.checkUndefinedProp(eventName, data);
    items.forEach((item) => this.checkUndefinedProp(eventName, item));
  }

  private checkUndefinedProp(eventName: string, data: Object) {
    for (const key in data) {
      if (!data.hasOwnProperty(key)) {
        continue;
      }
      const val = (data as Record<string, unknown>)[key];
      const invalid =
        val === undefined ||
        val === '' ||
        (typeof val === 'number' && !Number.isFinite(val)) ||
        (val != null &&
          typeof (val as { toString?: () => string }).toString === 'function' &&
          (val as { toString: () => string }).toString() === 'NaN');
      if (invalid) {
        const errorMsg = `[ GOOGLE TAGS EVENT ] : invalid property: ${key} on ${eventName}`;
        captureException(new Error(errorMsg));
        // Do not throw: blocking here prevented dataLayer pushes and GA/GTM hits.
        // console.warn(errorMsg, { key, val });
      }
    }
  }

  /** GA item names must be non-empty for GTM validators / downstream tags. */
  private toGaItemLabel(data: any): string {
    const raw = data?.name ?? data?.product_detail?.name;
    if (raw === null || raw === undefined) {
      return 'unknown';
    }
    const s = String(raw).trim();
    return s.length > 0 ? s : 'unknown';
  }

  private toGaItemId(data: any): string {
    const id = data?.id;
    if (id === null || id === undefined) {
      return 'unknown';
    }
    return String(id);
  }

  private toGaCurrencyCode(data: any): string {
    const raw = data?.currency_detail?.code;
    if (raw === null || raw === undefined) {
      return 'USD';
    }
    const s = String(raw).trim();
    return s.length > 0 ? s : 'USD';
  }

  /**
   * Resolves discount for GA payloads across list cards, ProductV2, and nested API shapes.
   */
  private resolveProductDiscount(data: any): number {
    const raw =
      data?.store_product_detail?.discount ??
      data?.product_detail?.store_product_detail?.discount ??
      data?.product_detail?.discount ??
      0;
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(n) ? n : 0;
  }

  private selectItemSerializer(data: any): SelectItemEvent {
    const itemName = this.toGaItemLabel(data);
    const discountValue = this.resolveProductDiscount(data);
    const indexNum = parseFloat(String(data?.index ?? '0'));
    return {
      items: [
        {
          item_id: this.toGaItemId(data),
          item_name: itemName,
          index: Number.isFinite(indexNum) ? indexNum : 0,
          discount: discountValue
        }
      ]
    };
  }

  private viewItemSerializer(data: any): DefaultEcommerceEvent {
    const itemName = this.toGaItemLabel(data);
    const discountValue = this.resolveProductDiscount(data);
    const firstVariety = data?.product_detail?.varieties?.[0];
    const minPriceRaw = firstVariety?.variety_detail?.min_price;
    const minPrice = parseFloat(String(minPriceRaw ?? '0'));
    return {
      currency: this.toGaCurrencyCode(data),
      value: Number.isFinite(minPrice) ? minPrice : 0,
      items: [
        {
          item_id: this.toGaItemId(data),
          item_name: itemName,
          discount: discountValue
        }
      ]
    };
  }

  private cartSerializer(data: any): DefaultEcommerceEvent {
    const discountNum = parseFloat(
      String(data?.store_product_detail?.discount ?? data?.discount ?? '0')
    );
    const priceNum = parseFloat(String(data?.price ?? '0'));
    const qty = Number(data?.quantity);
    return {
      currency: (data?.currency != null ? String(data.currency).trim() : '') || 'USD',
      value: parseFloat(String(data?.payablePrice ?? '0')) || 0,
      items: [
        {
          item_id: this.toGaItemId(data),
          item_name: this.toGaItemLabel(data),
          discount: Number.isFinite(discountNum) ? discountNum : 0,
          price: Number.isFinite(priceNum) ? priceNum : 0,
          quantity: Number.isFinite(qty) ? qty : 0
        }
      ]
    };
  }

  private paymentInfoSerializer(data: any): PaymentInfoEvent {
    return {
      currency: data.currency,
      value: parseFloat(data.payablePrice),
      payment_type: data.paymentType,
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          discount: parseFloat(data.discount),
          price: parseFloat(data.price),
          quantity: data.quantity
        }
      ]
    };
  }

  private shippingSerializer(data: any): ShippingInfoEvent {
    return {
      currency: data.currency,
      value: +data.payablePrice,
      shipping_tier: 'digital',
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          discount: parseFloat(data.discount),
          price: parseFloat(data.price),
          quantity: data.quantity
        }
      ]
    };
  }

  private purchaseSerializer(data: any): PurchaseEvent {
    return {
      currency: data.currency,
      value: parseFloat(data.payablePrice),
      transaction_id: data.transactionId,
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          discount: parseFloat(data.discount),
          price: parseFloat(data.price),
          quantity: data.quantity,
          payablePrice: data.payablePrice
        }
      ]
    };
  }

  private categoryProps(categoryList: any) {
    if (!categoryList || categoryList.length === 0) return;
    const categories: Record<string, string> = {};
    categoryList.forEach((el: { name?: string } | undefined, i: number) => {
      const name = el?.name;
      if (name !== undefined && name !== '') {
        categories[`item_category${i + 1}`] = name;
      }
    });
    return Object.keys(categories).length ? categories : undefined;
  }
}
